import * as Sentry from '@sentry/nextjs';
import { InternalLogger, ErrorLevel, AcceptableTags } from '../types';
import { AxiosError } from 'axios';
import { CUSTOM_URL_FINGERPRINT_HEADER } from 'constants/Http';

const LOW_CARDINALITY_TAG_KEYS = new Set([
  'errorLevel',
  'operation',
  'paymentMethod',
  'statusCode',
  'statusClass',
  'errorCode',
  'routeTemplate',
  'requestMethod',
  'flow',
]);

const HTTP_CONTEXT_KEYS = new Set([
  'requestUrl',
  'requestMethod',
  'statusCode',
  'customUrlFingerprint',
  'routeTemplate',
]);

const CHECKOUT_CONTEXT_KEYS = new Set([
  'operation',
  'paymentMethod',
  'errorCode',
  'paymentCode',
  'flow',
  'product',
  'planId',
  'subscriptionId',
  'leadId',
  'business',
]);

const DEBUG_CONTEXT_KEYS = new Set([
  'title',
  'type',
  'query',
  'params',
  'isServer',
  'componentStack',
  'originalException',
]);

export class SentryAdapter implements InternalLogger {
  constructor(private readonly logger = Sentry) {}

  private isAcceptableTagValue(
    value: unknown
  ): value is string | number | boolean {
    return (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    );
  }

  private getStatusClass(statusCode: unknown): string | undefined {
    if (typeof statusCode !== 'number' && typeof statusCode !== 'string') {
      return undefined;
    }

    const code = Number(statusCode);

    if (Number.isNaN(code)) {
      return undefined;
    }

    return `${Math.floor(code / 100)}xx`;
  }

  private normalizeAdditionalTags(
    errorLevel: ErrorLevel,
    additionalTags?: Record<string, AcceptableTags>,
    tagsFromInfo?: Record<string, AcceptableTags>
  ): Record<string, AcceptableTags> {
    const tags: Record<string, AcceptableTags> = {
      errorLevel,
    };

    const merged = {
      ...additionalTags,
      ...tagsFromInfo,
    };

    for (const [key, value] of Object.entries(merged)) {
      if (!LOW_CARDINALITY_TAG_KEYS.has(key)) continue;
      if (!this.isAcceptableTagValue(value)) continue;

      tags[key] = value;
    }

    return tags;
  }

  private normalizeAdditionalInfo(additionalInfo?: Record<string, unknown>): {
    user?: {
      id?: string;
      email?: string;
    };
    contexts: Record<string, Record<string, unknown>>;
    extra: Record<string, unknown>;
    tags: Record<string, AcceptableTags>;
  } {
    const info = additionalInfo ?? {};
    const contexts: Record<string, Record<string, unknown>> = {};
    const httpContext: Record<string, unknown> = {};
    const checkoutContext: Record<string, unknown> = {};
    const debugContext: Record<string, unknown> = {};
    const extra: Record<string, unknown> = {};
    const tags: Record<string, AcceptableTags> = {};

    const setTag = (key: string, value: unknown): void => {
      if (!LOW_CARDINALITY_TAG_KEYS.has(key)) return;
      if (!this.isAcceptableTagValue(value)) return;

      tags[key] = value;
    };

    const userEmail =
      typeof info?.userEmail === 'string'
        ? info.userEmail
        : typeof info?.email === 'string'
          ? info.email
          : undefined;

    const userId =
      typeof info?.userId === 'string' || typeof info?.userId === 'number'
        ? String(info.userId)
        : undefined;

    const user =
      userEmail || userId
        ? {
            ...(userId ? { id: userId } : {}),
            ...(userEmail ? { email: userEmail } : {}),
          }
        : undefined;

    for (const [key, value] of Object.entries(info)) {
      if (key === 'userEmail' || key === 'email' || key === 'userId') continue;

      if (HTTP_CONTEXT_KEYS.has(key)) {
        httpContext[key] = value;

        if (key === 'requestMethod' && typeof value === 'string') {
          setTag('requestMethod', value.toUpperCase());
          continue;
        }

        if (key === 'statusCode') {
          setTag('statusCode', String(value));
          const statusClass = this.getStatusClass(value);
          if (statusClass) {
            setTag('statusClass', statusClass);
          }
          continue;
        }

        setTag(key, value);
        continue;
      }

      if (CHECKOUT_CONTEXT_KEYS.has(key)) {
        checkoutContext[key] = value;
        setTag(key, value);
        continue;
      }

      if (DEBUG_CONTEXT_KEYS.has(key)) {
        debugContext[key] = value;
        continue;
      }

      if (key.toLowerCase().startsWith('extra')) {
        extra[key] = value;
        continue;
      }

      debugContext[key] = value;
    }

    if (Object.keys(httpContext).length > 0) {
      contexts.http = httpContext;
    }

    if (Object.keys(checkoutContext).length > 0) {
      contexts.checkout = checkoutContext;
    }

    if (Object.keys(debugContext).length > 0) {
      contexts.debug = debugContext;
    }

    return { user, contexts, extra, tags };
  }

  private setCustomFingerprintForHttpCalls(
    scope: Sentry.Scope,
    httpData: {
      requestUrl: string;
      customUrlFingerprint?: string;
      requestMethod: string;
      statusCode: string;
    }
  ): void {
    const formattedRequestUrl = httpData.requestUrl.split('?');
    const requestUrlWithoutQueryStr = formattedRequestUrl[0];
    const queryString = formattedRequestUrl[1];

    const requestUrlToGroupBy =
      httpData.customUrlFingerprint || requestUrlWithoutQueryStr;

    if (requestUrlWithoutQueryStr) {
      scope.setFingerprint([
        requestUrlToGroupBy,
        httpData.requestMethod,
        String(httpData.statusCode),
      ]);
    }
    if (queryString) {
      scope.setExtra('queryString', queryString);
    }
  }

  captureError(
    errorLevel: ErrorLevel,
    error: Error | string | AxiosError,
    additionalTags?: Record<string, AcceptableTags>,
    additionalInfo?: Record<string, unknown>
  ): void {
    const customUrlFingerprint: string | undefined = (error as AxiosError)
      .config?.headers?.[CUSTOM_URL_FINGERPRINT_HEADER];
    const url = (error as AxiosError).config?.url;
    const method = (error as AxiosError).config?.method;
    const statusCode = (error as AxiosError).response?.status;

    this.logger.withScope((scope) => {
      const normalizedInfo = this.normalizeAdditionalInfo(additionalInfo);
      const normalizedTags = this.normalizeAdditionalTags(
        errorLevel,
        additionalTags,
        normalizedInfo.tags
      );

      if (url && statusCode && method) {
        this.setCustomFingerprintForHttpCalls(scope, {
          requestUrl: url,
          customUrlFingerprint,
          requestMethod: method,
          statusCode: String(statusCode),
        });
      }

      if (normalizedInfo.user?.email || normalizedInfo.user?.id) {
        scope.setUser(normalizedInfo.user);
      }

      for (const [contextName, contextData] of Object.entries(
        normalizedInfo.contexts
      )) {
        if (Object.keys(contextData).length > 0) {
          scope.setContext(contextName, contextData);
        }
      }

      for (const [key, value] of Object.entries(normalizedInfo.extra)) {
        scope.setExtra(key, value);
      }

      if (Object.keys(normalizedTags).length > 0) {
        scope.setTags(normalizedTags);
      }

      scope.setLevel(errorLevel);
      scope.setExtra('isFromHandler', true);

      this.logger.captureException(error);
    });
  }
}
